// Memory auto-approve policies — a sub-page of the Config section's Agents
// folder (DEV-16823). A policy is OPT-IN: with no matching enabled policy an
// agent-authored memory still enters PENDING for human review. An ENABLED
// policy whose target (+ optional memory type) matches makes the write choke
// point create the memory APPROVED instead — skipping review for that narrow
// target. An empty list means nothing auto-approves (the safe default).
//
// This page owns the policy lifecycle: list (target + type + enabled toggle +
// delete) and a create form (targetKind → agent/collection/workflow+node
// picker → optional memory type). The write path that consults these policies
// lives in @cortex/database.
import { useClient } from '@cortex/react-query';
import type { MemoryType, WorkflowListItem } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { CompactSelect } from '../../components/common/CompactSelect';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { Table, type IColumn } from '../../components/common/Table';
import { useCollectionList } from '../../lib/agent-collection';
import { useAgentList } from '../../lib/agent-list';
import {
  useAutoApprovePolicies,
  useCreateAutoApprovePolicy,
  useDeleteAutoApprovePolicy,
  useToggleAutoApprovePolicy,
  type AgentMemoryAutoApprovePolicy,
  type AutoApproveTargetKind,
  type CreateAutoApprovePolicyInput,
} from '../../lib/auto-approve-policy';

export const Route = createFileRoute('/agents/auto-approve')({ component: AutoApprovePage });

// PRINCIPLE is deliberately absent: auto-approve resolves at CREATE time, and a
// memory can only become PRINCIPLE later (judge-stamped after the save) — a
// policy targeting it would never match, i.e. an inert policy the operator
// believes is doing something.
const memoryTypes: MemoryType[] = ['RULE', 'PREFERENCE', 'CONTEXT', 'NOTE'];
const targetKinds: AutoApproveTargetKind[] = ['AGENT', 'AGENT_KIND', 'COLLECTION', 'NODE', 'ALL'];

/** List workflows (id + name + node graph) to power the NODE picker. */
function useWorkflowList() {
  const client = useClient();
  return useQuery({
    queryKey: ['workflows', 'auto-approve-picker'],
    queryFn: () =>
      client.http
        .get('v1/workflows', { searchParams: { limit: '200' } })
        .json<{ items: WorkflowListItem[] }>()
        .then((page) => page.items),
  });
}

function AutoApprovePage() {
  const listQuery = useAutoApprovePolicies();
  const toggle = useToggleAutoApprovePolicy();
  const remove = useDeleteAutoApprovePolicy();
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AgentMemoryAutoApprovePolicy | null>(null);

  const agents = useAgentList({ includeSystem: true });
  const collections = useCollectionList();
  const workflows = useWorkflowList();

  const agentName = useMemo(
    () => new Map((agents.data ?? []).map((a) => [a.id, a.name])),
    [agents.data],
  );
  const collectionName = useMemo(
    () => new Map((collections.data ?? []).map((c) => [c.id, c.name])),
    [collections.data],
  );
  const workflowName = useMemo(
    () => new Map((workflows.data ?? []).map((w) => [w.id, w.name])),
    [workflows.data],
  );

  function describeTarget(p: AgentMemoryAutoApprovePolicy): string {
    switch (p.targetKind) {
      case 'AGENT':
        return `Agent · ${agentName.get(p.agentId ?? '') ?? p.agentId ?? '—'}`;
      case 'COLLECTION':
        return `Collection · ${collectionName.get(p.collectionId ?? '') ?? p.collectionId ?? '—'}`;
      case 'NODE':
        return `Node · ${workflowName.get(p.workflowId ?? '') ?? p.workflowId ?? '—'} / ${p.nodeKey ?? '—'}`;
      case 'ALL':
        return 'Every agent-authored memory';
      case 'AGENT_KIND':
        // DEV-17256: population by kind; reach bounded server-side by the
        // self-scope predicate (ORCHESTRATION) / userSubject (CHAT).
        return p.agentKind === 'CHAT'
          ? 'Every CHAT agent · user-scoped saves only'
          : `Every ${(p.agentKind ?? '—').toLowerCase()} agent · self-scoped saves only`;
    }
  }

  const cols: IColumn<AgentMemoryAutoApprovePolicy>[] = [
    {
      key: 'target',
      header: 'Target',
      cell: (p) => <span className="t-strong">{describeTarget(p)}</span>,
    },
    {
      key: 'type',
      header: 'Memory type',
      cell: (p) => (
        <span className="t-muted">{p.memoryType ? p.memoryType.toLowerCase() : 'all types'}</span>
      ),
    },
    {
      key: 'enabled',
      header: 'Enabled',
      cell: (p) => (
        <label
          style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={p.enabled}
            disabled={toggle.isPending}
            onChange={(e) => toggle.mutate({ id: p.id, enabled: e.target.checked })}
          />
          <span className="t-xs">{p.enabled ? 'On' : 'Off'}</span>
        </label>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: 80,
      cell: (p) => (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={(e) => {
            e.stopPropagation();
            setConfirmDelete(p);
          }}
        >
          Delete
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Auto-approve"
        actions={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => setCreating(true)}
          >
            New policy
          </button>
        }
      />
      <Table<AgentMemoryAutoApprovePolicy>
        rows={listQuery.data}
        columns={cols}
        rowKey={(p) => p.id}
        isLoading={listQuery.isLoading}
        error={(listQuery.error as Error | null) ?? null}
        emptyTitle="No auto-approve policies"
        emptyDescription="Every agent-authored memory waits for human review by default. Add a policy to auto-approve memories for a specific agent, collection, or workflow node."
      />
      {creating ? (
        <CreatePolicyModal
          agents={(agents.data ?? []).map((a) => ({ id: a.id, name: a.name }))}
          collections={(collections.data ?? []).map((c) => ({ id: c.id, name: c.name }))}
          workflows={workflows.data ?? []}
          onClose={() => setCreating(false)}
        />
      ) : null}
      <Modal
        open={confirmDelete !== null}
        onClose={() => {
          if (!remove.isPending) setConfirmDelete(null);
        }}
        title={confirmDelete ? 'Delete auto-approve policy?' : ''}
        width="min(480px, 92vw)"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
            Memories matching this policy will go back to requiring human approval on their next
            write. Existing approved memories are untouched.
          </p>
          {remove.error ? (
            <div className="field-error">{(remove.error as Error).message}</div>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setConfirmDelete(null)}
              disabled={remove.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--danger btn--sm"
              disabled={remove.isPending}
              onClick={() => {
                if (!confirmDelete) return;
                remove.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) });
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete policy'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

interface INamed {
  id: string;
  name: string;
}

function CreatePolicyModal({
  agents,
  collections,
  workflows,
  onClose,
}: {
  agents: INamed[];
  collections: INamed[];
  workflows: WorkflowListItem[];
  onClose: () => void;
}) {
  const create = useCreateAutoApprovePolicy();
  const [targetKind, setTargetKind] = useState<AutoApproveTargetKind>('AGENT');
  const [agentId, setAgentId] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [nodeKey, setNodeKey] = useState('');
  const [agentKind, setAgentKind] = useState('');
  const [memoryType, setMemoryType] = useState<MemoryType | ''>('');
  const [error, setError] = useState<string | null>(null);

  const nodeOptions = useMemo(() => {
    const wf = workflows.find((w) => w.id === workflowId);
    return (wf?.graphSummary?.nodes ?? []).map((n) => ({
      value: n.nodeKey,
      label: n.nodeKey,
      description: n.nodeType,
    }));
  }, [workflows, workflowId]);

  const canSave = (() => {
    switch (targetKind) {
      case 'AGENT':
        return agentId !== '';
      case 'COLLECTION':
        return collectionId !== '';
      case 'NODE':
        return workflowId !== '' && nodeKey !== '';
      case 'AGENT_KIND':
        return agentKind !== '';
      case 'ALL':
        return true;
    }
  })();

  async function save() {
    if (!canSave) return;
    setError(null);
    const base = { memoryType: memoryType === '' ? undefined : memoryType };
    let body: CreateAutoApprovePolicyInput;
    switch (targetKind) {
      case 'AGENT':
        body = { targetKind, agentId, enabled: true, ...base };
        break;
      case 'COLLECTION':
        body = { targetKind, collectionId, enabled: true, ...base };
        break;
      case 'NODE':
        body = { targetKind, workflowId, nodeKey, enabled: true, ...base };
        break;
      case 'AGENT_KIND':
        body = {
          targetKind,
          agentKind: agentKind as 'ORCHESTRATION' | 'CHAT',
          enabled: true,
          ...base,
        };
        break;
      case 'ALL':
        body = { targetKind, enabled: true, ...base };
        break;
    }
    try {
      await create.mutateAsync(body);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title="New auto-approve policy"
      eyebrow="Skip human review for a narrow target"
      width="min(560px, 92vw)"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span className="t-muted t-xs">Applies to</span>
          <select
            className="input"
            value={targetKind}
            onChange={(e) => setTargetKind(e.target.value as AutoApproveTargetKind)}
          >
            {targetKinds.map((k) => (
              <option key={k} value={k}>
                {k === 'ALL'
                  ? 'Every agent (org-wide)'
                  : k === 'AGENT_KIND'
                    ? 'Every agent of a kind (self-scoped)'
                    : k.charAt(0) + k.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </label>

        {targetKind === 'AGENT' ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
            <span className="t-muted t-xs">Agent</span>
            <CompactSelect
              value={agentId}
              onChange={setAgentId}
              options={agents.map((a) => ({ value: a.id, label: a.name }))}
              placeholder="Select an agent…"
              width={480}
            />
          </label>
        ) : null}

        {targetKind === 'COLLECTION' ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
            <span className="t-muted t-xs">Collection</span>
            <CompactSelect
              value={collectionId}
              onChange={setCollectionId}
              options={collections.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Select a collection…"
              width={480}
            />
          </label>
        ) : null}

        {targetKind === 'NODE' ? (
          <>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
              <span className="t-muted t-xs">Workflow</span>
              <CompactSelect
                value={workflowId}
                onChange={(v) => {
                  setWorkflowId(v);
                  setNodeKey('');
                }}
                options={workflows.map((w) => ({ value: w.id, label: w.name }))}
                placeholder="Select a workflow…"
                width={480}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
              <span className="t-muted t-xs">Node</span>
              {nodeOptions.length > 0 ? (
                <CompactSelect
                  value={nodeKey}
                  onChange={setNodeKey}
                  options={nodeOptions}
                  placeholder="Select a node…"
                  width={480}
                />
              ) : (
                <input
                  className="input"
                  value={nodeKey}
                  onChange={(e) => setNodeKey(e.target.value)}
                  placeholder="Node key (e.g. summarize)"
                  disabled={workflowId === ''}
                />
              )}
            </label>
          </>
        ) : null}

        {targetKind === 'AGENT_KIND' ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
            <span className="t-muted t-xs">Agent kind</span>
            <select
              className="input"
              value={agentKind}
              onChange={(e) => setAgentKind(e.target.value)}
            >
              <option value="" disabled={true}>
                Select a kind…
              </option>
              <option value="ORCHESTRATION">
                Orchestration — self-scoped saves only (fleet self-learning)
              </option>
              <option value="CHAT">Chat — user-scoped personal preferences only</option>
            </select>
            <span className="t-muted t-xs">
              Reach is bounded server-side: orchestration saves auto-approve only when they bind
              nobody but the authoring agent; chat saves only when self-scoped AND pinned to one
              person. Org-wide proposals always cross human review.
            </span>
          </label>
        ) : null}

        <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
          <span className="t-muted t-xs">Memory type</span>
          <select
            className="input"
            value={memoryType}
            onChange={(e) => setMemoryType(e.target.value as MemoryType | '')}
          >
            <option value="">All types</option>
            {memoryTypes.map((t) => (
              <option key={t} value={t}>
                {t.toLowerCase()}
              </option>
            ))}
          </select>
        </label>

        {error ? <div className="field-error">{error}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={save}
            disabled={!canSave || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create policy'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
